import { Injectable } from '@angular/core';
import { ApiService } from '../../../../core/services/api.service';
import { Observable } from 'rxjs';
import { ISubcontractor, IImportResult, IProtectedField, ISubcontractorFilters, ISubcontractorHMRC, ICertificate } from './types';
import {
    IFilters,
    ILastLogins,
    IPagination,
    IQuestionnaire,
    IQuestionnaireUser,
    IQuestionnaireUserAnswer,
} from '../../../../modules/authorization/store/types';
import { map, tap } from 'rxjs/operators';
import { AuthorizationState } from '../../../authorization/store/state';
import { RolesService } from '../../../../core/services/roles.service';
import { Store } from '@ngxs/store';
import { IWorkLocation } from 'src/app/modules/logged/work-locations/store/types';
import { IAgreementUser } from 'src/app/modules/logged/agreements/store/types';

@Injectable({
    providedIn: 'root',
})
export class SubcontractorService {
    constructor(private apiService: ApiService, public rolesService: RolesService, private store: Store) {}

    public fetchAll(data: ISubcontractorFilters): Observable<IPagination<ISubcontractor>> {
        return this.apiService.get('/subcontractors', data);
    }

    public fetchAllByContractStatus(active: boolean = true, data: ISubcontractorFilters): Observable<IPagination<ISubcontractor>> {
        return this.apiService.get(`/subcontractors/statistics/subcontractorsContractStats?active=${active}`, data);
    }

    public fetchAllChangeRequestsNumber(): Observable<number> {
        return this.apiService.get(`/subcontractors/statistics/getAllChangeRequestsList`).pipe(map((res: IPagination) => res.total));
    }

    public fetchAllAssigned(siteManagerID: string, data: ISubcontractorFilters): Observable<IPagination<ISubcontractor>> {
        return this.apiService.get(`/subcontractors/${siteManagerID}/assigned`, data);
    }

    public fetchTimeRecordsForSiteManager(data: ISubcontractorFilters): Observable<IPagination<ISubcontractor>> {
        return this.apiService.get(`/subcontractors/assigned/time-records`, data);
    }

    public fetchWorkLocationsSiteManager(siteManagerID: string, data: IFilters): Observable<IPagination<IWorkLocation>> {
        return this.apiService.get(`/subcontractors/${siteManagerID}/locations`, data);
    }

    public fetchOne(id: string): Observable<{
        protectedFields: IProtectedField[];
        subcontractor: ISubcontractor;
        agreementUser: IAgreementUser;
        changeRequestsFormated?: {}[];
        lastLogins: ILastLogins;
    }> {
        return this.apiService.get(`/subcontractors/${id}`);
    }

    public fetchHMRC(id: string): Observable<ISubcontractorHMRC> {
        return this.apiService.get(`/subcontractors/${id}/hmrc`).pipe(
            map((response: { SubContractor: ISubcontractorHMRC }) => {
                return response.SubContractor;
            }),
        );
    }

    public add(id: string, data: any): Observable<any> {
        data.cis_type = data.cis_type ? 'Gross' : 'Net';
        data.identity_document_type = data.identity_document_type ? data.identity_document_type : 'NULL';
        return this.apiService.post(`/companies/${id}/subcontractors`, data).pipe(
            tap((subcontractor: ISubcontractor) => {
                if (data.identity_verification_document !== 'NULL' && data.identity_verification_document !== null) {
                    this.uploadPropertyFile(subcontractor._id, 'identity_verification_document', data.identity_verification_document).subscribe();
                }
                if (data.vehicle_verification_document !== 'NULL' && data.vehicle_verification_document !== null) {
                    this.uploadPropertyFile(subcontractor._id, 'vehicle_verification_document', data.vehicle_verification_document).subscribe();
                }
                if (data.vat_verification_document !== 'NULL' && data.vat_verification_document !== null) {
                    this.uploadPropertyFile(subcontractor._id, 'vat_verification_document', data.vat_verification_document).subscribe();
                }
                if (data.gross_verification_document !== 'NULL' && data.gross_verification_document !== null) {
                    this.uploadPropertyFile(subcontractor._id, 'gross_verification_document', data.gross_verification_document).subscribe();
                }
            }),
        );
    }

    public update(id: string, data: any, notFromChangeRequests: boolean = true): Observable<any> {
        if (notFromChangeRequests) {
            data.cis_type = data.cis_type ? 'Gross' : 'Net';
            data.identity_document_type = data.identity_document_type ? data.identity_document_type : 'NULL';
        }
        return this.apiService.put(`/subcontractors/${id}`, data).pipe(
            tap(() => {
                if (
                    data.hasOwnProperty('identity_verification_document') &&
                    typeof data.identity_verification_document !== 'string' &&
                    data.identity_verification_document
                ) {
                    this.uploadUpdatedPropertyFile(id, 'identity_verification_document', data.identity_verification_document).subscribe();
                }
                if (
                    data.hasOwnProperty('vehicle_verification_document') &&
                    typeof data.vehicle_verification_document !== 'string' &&
                    data.vehicle_verification_document
                ) {
                    this.uploadUpdatedPropertyFile(id, 'vehicle_verification_document', data.vehicle_verification_document).subscribe();
                }
                if (data.hasOwnProperty('vat_verification_document') && typeof data.vat_verification_document !== 'string' && data.vat_verification_document) {
                    this.uploadUpdatedPropertyFile(id, 'vat_verification_document', data.vat_verification_document).subscribe();
                }
                if (
                    data.hasOwnProperty('gross_verification_document') &&
                    typeof data.gross_verification_document !== 'string' &&
                    data.gross_verification_document
                ) {
                    this.uploadUpdatedPropertyFile(id, 'gross_verification_document', data.gross_verification_document).subscribe();
                }
            }),
        );
    }

    public updateChangeRequest(id: string, data: any, param?: {}): Observable<any> {
        return this.apiService.put(`/subcontractors/${id}`, data, param).pipe(
            tap(() => {
                if (data.hasOwnProperty('identity_verification_document') && typeof data.identity_verification_document !== 'string') {
                    this.uploadUpdatedPropertyFile(id, 'identity_verification_document', data.identity_verification_document).subscribe();
                }
                if (data.hasOwnProperty('vehicle_verification_document') && typeof data.vehicle_verification_document !== 'string') {
                    this.uploadUpdatedPropertyFile(id, 'vehicle_verification_document', data.vehicle_verification_document).subscribe();
                }
                if (data.hasOwnProperty('vat_verification_document') && typeof data.vat_verification_document !== 'string') {
                    this.uploadUpdatedPropertyFile(id, 'vat_verification_document', data.vat_verification_document).subscribe();
                }
                if (data.hasOwnProperty('gross_verification_document') && typeof data.gross_verification_document !== 'string') {
                    this.uploadUpdatedPropertyFile(id, 'gross_verification_document', data.gross_verification_document).subscribe();
                }
            }),
        );
    }

    public register(data: any): Observable<any> {
        data.cis_type = data.cis_type ? 'Gross' : 'Net';
        data.identity_document_type = data.identity_document_type ? data.identity_document_type : 'NULL';
        return this.apiService.post(`/companies/${data.code}/subcontractors/register`, data).pipe(
            tap(({ _id }: { _id: string }) => {
                if (data.identity_verification_document) {
                    this.uploadPropertyFile(_id, 'identity_verification_document', data.identity_verification_document).subscribe();
                }
                if (data.vehicle_verification_document) {
                    this.uploadPropertyFile(_id, 'vehicle_verification_document', data.vehicle_verification_document).subscribe();
                }
                if (data.vat_verification_document) {
                    this.uploadPropertyFile(_id, 'vat_verification_document', data.vat_verification_document).subscribe();
                }
                if (data.gross_verification_document) {
                    this.uploadPropertyFile(_id, 'gross_verification_document', data.gross_verification_document).subscribe();
                }
            }),
        );
    }

    public signContract(id: string, signature: string): Observable<any> {
        return this.apiService.post(`/subcontractors/${id}/sign`, { signature: signature });
    }

    public uploadPropertyFile(id: string, property: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file || '');
        return this.apiService.put(`/subcontractors/${id}/files/${property}`, formData);
    }

    public uploadUpdatedPropertyFile(id: string, property: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file || '');

        const params = {};

        if (
            this.rolesService.checkRoles(this.store.selectSnapshot(AuthorizationState.role), [
                this.rolesService.roles.COMPANY,
                this.rolesService.roles.COMPANY_USER_A,
            ])
        ) {
            params['company_panel_update'] = 1;
        }

        return this.apiService.put(`/subcontractors/${id}/files/${property}`, formData, params);
    }

    public import(id: string, file: File): Observable<IImportResult> {
        const formData = new FormData();
        formData.append('subcontractors', file);
        return this.apiService.post(`/companies/${id}/subcontractors/import`, formData);
    }

    public delete(id: string): Observable<any> {
        return this.apiService.delete(`/subcontractors/${id}`);
    }

    public downloadPayslips(id: string): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors/${id}/payslips/pdf`);
    }

    public resendEmailLink(id: string): Observable<any> {
        return this.apiService.post(`/subcontractors/${id}/registration-resend`);
    }

    public sendSMS(id: string, data: any): Observable<any> {
        return this.apiService.post(`/subcontractors/${id}/notifications/sms`, data);
    }

    public resendNotification(subcontractorId: string, payrollId: string, data: any): Observable<any> {
        return this.apiService.post(`/subcontractors/${subcontractorId}/${payrollId}/notifications/email-sms`, data);
    }

    public sendNotification(id: string, data: any): Observable<any> {
        return this.apiService.post(`/subcontractors/${id}/notifications/push`, data);
    }

    public bulkAction(action: string, ids: string[], data?: any): Observable<any> {
        return this.apiService.post(`/subcontractors/bulk/${action}`, { ids, ...data });
    }

    public bulkDownload(action: string, ids: string[], data?: any): Observable<any> {
        return this.apiService.downloadPost(`/subcontractors/bulk/${action}`, { ids, ...data });
    }

    public exportSubcontractors(): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors/export/csv`);
    }

    public exportSubcontractorsToXero(data: any): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors/export/xero`, data);
    }

    public exportSubcontractorsToCSV(data: any): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors`, data);
    }

    public exportSubcontractorsInactiveToCSV(data: any): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors`, data);
    }

    public exportSubcontractorsContractActiveToCSV(data: any): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors/statistics/subcontractorsContractStats?active=true`, data);
    }

    public exportSubcontractorsContractInactiveToCSV(data: any): Observable<any> {
        return this.apiService.downloadGet(`/subcontractors/statistics/subcontractorsContractStats?active=false`, data);
    }

    public changeRequest(id: string, data: any): Observable<any> {
        return this.apiService.post(`/subcontractors/${id}/change-request`, data);
    }

    public getSubcontractorContract(url: string): Observable<any> {
        return this.apiService.downloadGet(`/uploads${url}`);
    }

    public getSubcontractorOldFiles(url: string): Observable<any> {
        return this.apiService.downloadGet(`/uploads/${url}`);
    }

    public fetchAllRequests(data: ISubcontractorFilters): Observable<any> {
        return this.apiService.get(`/subcontractors/statistics/getAllChangeRequestsList`, data).pipe(
            map((res) => {
                res.results = res.results.map((item) => {
                    return {
                        _id: item._id,
                        email: item.email,
                        fields_amount: item.fieldChangeRequests.length,
                        fullname: item.fullname,
                        fields: item.fieldChangeRequests.map((request) => {
                            if (request.type) {
                                switch (request.type) {
                                    case 'VAT DATA UPDATE':
                                        return 'VAT details';
                                    case 'VAT DOCUMENT UPDATE':
                                        return 'VAT document';
                                    case 'GROSS DATA UPDATE':
                                        return 'Gross details';
                                    case 'GROSS DOCUMENT UPDATE':
                                        return 'Gross document';
                                    case 'IDENTITY DATA UPDATE':
                                        return 'Identity details';
                                    case 'IDENTITY DOCUMENT UPDATE':
                                        return 'Identity document';
                                    case 'VEHICLE DATA UPDATE':
                                        return 'Vehicle details';
                                    case 'VEHICLE DOCUMENT UPDATE':
                                        return 'Vehicle document';
                                    case 'COMPANY DETAILS PROFILE UPDATE':
                                        return 'Company profile details';
                                    case 'COMPANY DETAILS DATA UPDATE':
                                        return 'Company data details';
                                }
                            } else {
                                return request.field.replace(/\_/g, ' ').replace(/\./g, '->');
                            }
                        }),
                    };
                });
                return res;
            }),
        );
    }

    public getFieldStatus(field: string, fieldsStatuses: { [k: string]: string }[]): string | null {
        if (fieldsStatuses) {
            let resultStatus = null;

            if (
                field === 'company_details.name_status' ||
                field === 'company_details.registration_number_status' ||
                field === 'company_details.UTR_number_status'
            ) {
                fieldsStatuses.forEach((object) => {
                    const key = Object.keys(object)[0];
                    if (key === field) {
                        if (object[key]?.toUpperCase() === 'IN VERIFICATION') {
                            resultStatus = object[key];
                        } else {
                            const found = fieldsStatuses.filter((el) => Object.keys(el)[0] === 'business_type_status');
                            if (Object.values(found[0])[0]?.toUpperCase() === 'IN VERIFICATION') {
                                resultStatus = Object.values(found[0])[0];
                            } else {
                                return null;
                            }
                        }
                    }
                });
            } else {
                fieldsStatuses.forEach((object) => {
                    const key = Object.keys(object)[0];

                    if (key === field) {
                        if (object[key]?.toUpperCase() === 'IN VERIFICATION') {
                            resultStatus = object[key];
                        } else {
                            return null;
                        }
                    }
                });
            }

            return resultStatus;
        } else {
            return null;
        }
    }

    public checkUniqueNumbers(data: any): Observable<{ taken: boolean }> {
        return this.apiService.post('/subcontractors/check-unique-numbers', {
            field: data.field,
            value: data.value,
            subcontractorID: data.subcontractorID,
        });
    }

    public verifyHMRC(id: string): Observable<boolean> {
        return this.apiService.get(`/subcontractors/${id}/verify-hmrc`);
    }

    public verifyVAT(id: string): Observable<boolean> {
        return this.apiService.get(`/subcontractors/${id}/verify-vat`);
    }

    public addOtherFile(id: string, data: { file: File; description: string }): Observable<any> {
        const formData = new FormData();
        formData.append('file', data.file || '');
        formData.append('description', data.description || '');
        return this.apiService.put(`/subcontractors/${id}/otherFiles/other_files`, formData);
    }

    public removeOtherFile(id: string, fileId): Observable<any> {
        return this.apiService.delete(`/subcontractors/${id}/removeOtherFiles/${fileId}`);
    }

    public assignSubcontractors(siteManagerID: string, subcontractors: string[], unassign = false): Observable<boolean> {
        return this.apiService.post(`/subcontractors/${siteManagerID}/assigned`, { subcontractors, unassign });
    }

    public globalMessage(form: any): Observable<boolean> {
        return this.apiService.post(`/subcontractors/global-message`, form);
    }

    public fetchQuestionnaire(companyID: string): Observable<IQuestionnaire[]> {
        return this.apiService.get(`/questionnaire`, { companyID }).pipe(
            map((data: { questionnaire: IQuestionnaire[] }) => {
                data.questionnaire.forEach((item: IQuestionnaire) => {
                    item.suggestedAnswer = JSON.parse(item.suggestedAnswer);
                    if (item.type === 'DROPDOWN') {
                        item.suggestedAnswer = item.suggestedAnswer.map((el: string) => ({ title: el, value: el }));
                    }
                    return item;
                });
                return data.questionnaire;
            }),
        );
    }

    public saveQuestionnaire(subcontractorID: string, data: any): Observable<boolean> {
        return this.apiService.post(`/questionnaire/my`, { subcontractorID, answers: data });
    }

    public fetchMyQuestionnaire(): Observable<IQuestionnaireUser> {
        return this.apiService.get(`/questionnaire/my`).pipe(
            map((data: IQuestionnaireUser) => {
                if (data) {
                    data.answers = data.answers.map((item: IQuestionnaireUserAnswer) => {
                        if (item.answer === 'true') {
                            item.answer = true;
                        } else if (item.answer === 'false') {
                            item.answer = false;
                        }
                        return item;
                    });
                }
                return data;
            }),
        );
    }

    public fetchSubcontractorQuestionnaire(id: string): Observable<IQuestionnaireUser> {
        return this.apiService.get(`/questionnaire/subcontractor/${id}`).pipe(
            map((data: IQuestionnaireUser) => {
                data.answers = data.answers.map((item: IQuestionnaireUserAnswer) => {
                    if (item.answer === 'true') {
                        item.answer = true;
                    } else if (item.answer === 'false') {
                        item.answer = false;
                    }
                    return item;
                });
                return data;
            }),
        );
    }

    public changeSubcontractorPassword(id: string, data: any): Observable<any> {
        return this.apiService.put(`/subcontractors/${id}/password`, data);
    }

    public changeUserPassword(id: string, data: any): Observable<any> {
        return this.apiService.put(`/user/${id}/password`, data);
    }

    public getEmailChangesReport(): Observable<any> {
        return this.apiService.downloadGet(`/logs/email-changes/csv`);
    }

    public getCertificates(data?: any): Observable<ICertificate[]> {
        return this.apiService.get(`/certificates`, data).pipe(map((response: { certificates: ICertificate[] }) => response.certificates));
    }

    public getUploadedFile(url: string): Observable<any> {
        return this.apiService.downloadGet(`/uploads${url}`);
    }

    public getSubcontractorsCertificates(data?: any): Observable<IPagination<ISubcontractor>> {
        return this.apiService.get(`/subcontractors/certificates`, data);
    }
}
